import { useState } from "react";
import { api, type PersonEntry } from "../api";
import { usePagedList, useShowMore } from "../pagedList";
import { ShowMore } from "../ShowMore";
import { type MessageScope } from "../PeoplePanel";
import { initials, PersonMenu } from "../PersonMenu";
import { errorText, useT } from "../i18n";
import { useSession } from "../session";
import { navigate } from "../router";
import { formatDay } from "../time";

type PeopleTab = "following" | "followers" | "blocked" | "hidden";
const TABS: PeopleTab[] = ["following", "followers", "blocked", "hidden"];

/** People (PLAN.md 4.16 K9): whom I follow, who follows me, whom I block and whom I hide. */
export function Following({ onView }: { onView: (scope: MessageScope) => void }) {
  const t = useT();
  const { me, rememberMembers, followed, followMember, blocked, hidden, blockMember, hideMember, lockGeneration } = useSession();
  const [tab, setTab] = useState<PeopleTab>("following");
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Followers: paged on the server (PLAN.md 4.20), each with their directory entry, so the page knows their names and keys.
  const followerPages = usePagedList(`followers:${me.id}`, async (cursor) => {
    const page = await api.followers(cursor);
    rememberMembers(page.items.map(({ member_id, ...m }) => ({ ...m, id: member_id })));
    return page;
  }, tab === "followers");
  const followers = followerPages.items;

  async function act(id: number, fn: () => Promise<void>) {
    setBusy(id); setError(null);
    try { await fn(); } catch (err) { setError(errorText(err)); } finally { setBusy(null); }
  }

  const held: PersonEntry[] = tab === "following" ? Array.from(followed.values()) : tab === "followers" ? [] : Array.from((tab === "blocked" ? blocked : hidden).values());
  // Follows, blocks and hides are held whole (at most 500 or 1000); shown 50 at a time.
  const heldPage = useShowMore(held);
  const entries = tab === "followers" ? followers ?? [] : heldPage.shown;

  return <div className="following-page account-page">
    <div className="page-head"><h1>{t.design.followingTitle}</h1><p className="muted">{t.design.followingNote}</p></div>
    <div className="chips list-tabs" role="group" aria-label={t.design.followingTitle}>
      {TABS.map((value) => <button key={value} className={tab === value ? "chip on" : "chip"} aria-pressed={tab === value} onClick={() => setTab(value)}>{t.people.tabs[value]}</button>)}
    </div>
    <p className="small muted">{t.people.notes[tab]}</p>
    {(error || followerPages.error) && <p className="error">{error || followerPages.error}</p>}
    {tab === "followers" && followers === null && !followerPages.error && <p className="small muted">{t.common.loading}</p>}
    {entries.length === 0 && (tab !== "followers" || followers !== null) && <p className="muted">{t.people.none[tab]}</p>}
    <ul className="followed-list">{entries.map((entry) => {
      const name = entry.display_name || `#${entry.member_id}`;
      const id = entry.member_id;
      let action: { label: string; run: () => Promise<void> } | null = null;
      if (tab === "following") action = { label: t.capsules.unfollow(name), run: () => followMember(id, false) };
      else if (tab === "followers" && !followed.has(id)) action = { label: t.people.followBack(name), run: () => followMember(id, true) };
      else if (tab === "blocked") action = { label: t.design.unblock(name), run: () => blockMember(id, false) };
      else if (tab === "hidden") action = { label: t.design.unhide(name), run: () => hideMember(id, false) };
      return <li key={id}>
        <PersonMenu name={name} memberId={id} canWrite={!!entry.pubkey && followed.has(id)} onView={() => onView({ memberIds: [id], label: name, generation: lockGeneration })} onWrite={() => navigate("/compose", { recipientIds: [id] })}>
          <span className="avatar" aria-hidden="true">{initials(name)}</span><span>{name}</span>
        </PersonMenu>
        <span className="small muted">{t.people.since(formatDay(entry.created_at * 1000))}</span>
        {action && <button className="small" disabled={busy !== null} onClick={() => void act(id, action.run)}>{action.label}</button>}
      </li>;
    })}</ul>
    {tab === "followers" ? <ShowMore hasMore={followerPages.hasMore} busy={followerPages.loadingMore} onMore={() => void followerPages.loadMore()} /> : <ShowMore hasMore={heldPage.hasMore} onMore={heldPage.showMore} />}
  </div>;
}
